import type {
    IncomeSplitConfig,
    IncomeSplitRule,
} from '@fruitful/income-split';
import type { MoneyMapWithRulesAndAccounts } from '@fruitful/types';
import { useEffect, useState } from 'react';

import { requestBuilder } from '../../http/RequestBuilder';

const getProposedIncomeSplit = requestBuilder.query<
    IncomeSplitConfig,
    { moneyMapId: string; membershipId: string }
>({
    endpoint: ({ moneyMapId, membershipId }) =>
        `/api/admin/money-map/${membershipId}/${moneyMapId}/income-split`,
    queryKey: ({ moneyMapId }) => `useGetProposedIncomeSplit/${moneyMapId}`,
});

const useGetProposedIncomeSplit = (
    moneyMap: MoneyMapWithRulesAndAccounts | undefined,
) => {
    const [transformedIncomeSplitConfig, setTransformedIncomeSplitConfig] =
        useState<IncomeSplitConfig | null>(null);

    const {
        isLoading,
        data: incomeSplit,
        isError,
        error,
    } = getProposedIncomeSplit(
        {
            moneyMapId: moneyMap?.id ?? '',
            membershipId: moneyMap?.membershipId ?? '',
        },
        { enabled: (moneyMap?.moneyMapRuleGroups ?? []).length > 0 },
    );

    useEffect(() => {
        const transformRule = (rule: IncomeSplitRule): IncomeSplitRule => {
            return {
                ...rule,
                percentage: parseFloat(
                    ((rule?.percentage ?? 0) * 100).toFixed(0),
                ),
            };
        };

        if (incomeSplit) {
            const transformedConfig = {
                ...incomeSplit,
                ruleSets: incomeSplit?.ruleSets?.map((group) => ({
                    ...group,
                    rules: group.rules
                        .map(transformRule)
                        .sort((a, b) => a.priority - b.priority),
                })),
            };
            setTransformedIncomeSplitConfig(transformedConfig);
        }
    }, [incomeSplit]);

    return {
        data: transformedIncomeSplitConfig,
        isLoading,
        isError,
        error,
    };
};

export default useGetProposedIncomeSplit;
